import React, { useState, useEffect } from 'react'
import { Card, Table, Tag, Modal, Descriptions, Button, Typography, message, Spin, Input } from 'antd'
import { EyeOutlined, UserSwitchOutlined, LockOutlined } from '@ant-design/icons'
import { backend } from '../../api'

const { Title } = Typography

const ROLE_COLORS = { admin: 'red', member: 'blue' }
const ROLE_NAMES = { admin: '管理员', member: '会员' }
const LEVEL_COLORS = { basic: 'default', vip: 'gold', pro: 'geekblue', admin: 'red', enterprise: 'purple', ultimate: 'cyan' }
const LEVEL_NAMES = { basic: '基础版', vip: '会员版', pro: '专业版', admin: '管理员', enterprise: '企业版', ultimate: '旗舰版' }

function getUserLevel(user) {
  if (user?.role === 'admin') return 'admin'
  return user?.membership_level || user?.level || 'basic'
}

function getUserExpireAt(user) {
  return user?.membership_expire || user?.expire_at || user?.expires_at || user?.expire_time || user?.valid_until || null
}

function calcRemainingDays(expireAt, user) {
  if (user?.role === 'admin') return '永久'
  if (!expireAt) return '0 天'
  const now = Date.now()
  const exp = new Date(expireAt).getTime()
  const diff = Math.ceil((exp - now) / (1000 * 60 * 60 * 24))
  return diff <= 0 ? '已过期' : `${diff} 天`
}

function formatExpireAt(expireAt, user) {
  if (user?.role === 'admin') return '永久'
  if (expireAt) return new Date(expireAt).toLocaleDateString()
  return '未开通'
}

export default function MemberManagement() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [detailVisible, setDetailVisible] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [resetVisible, setResetVisible] = useState(false)
  const [resetTarget, setResetTarget] = useState(null)
  const [resetPassword, setResetPassword] = useState('')
  const [resetting, setResetting] = useState(false)

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const res = await backend.adminGetUsers()
      if (res.users) {
        setUsers(res.users)
      } else if (Array.isArray(res)) {
        setUsers(res)
      } else {
        setUsers([])
      }
    } catch (err) {
      message.error('获取会员列表失败: ' + (err.message || '未知错误'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchUsers() }, [])

  const showDetail = async (user) => {
    setSelectedUser(null)
    setDetailVisible(true)
    setDetailLoading(true)
    try {
      const res = await backend.adminGetUser(user.id || user.user_id)
      const detail = res.user || res.data || res
      setSelectedUser(detail)
    } catch (err) {
      message.error('获取用户详情失败')
    } finally {
      setDetailLoading(false)
    }
  }

  const openResetPassword = (user) => {
    setResetTarget(user)
    setResetPassword('')
    setResetVisible(true)
  }

  const handleResetPassword = async () => {
    if (!resetPassword.trim() || resetPassword.trim().length < 6) { message.warning('新密码至少6位'); return }
    setResetting(true)
    try {
      const res = await backend.adminResetPassword(resetTarget.id || resetTarget.user_id, resetPassword.trim())
      if (res.success) {
        message.success(`已重置 ${resetTarget.username} 的密码`)
        setResetVisible(false)
        setResetTarget(null)
      } else {
        message.error(res.error || '密码重置失败')
      }
    } catch (e) { message.error('密码重置失败') }
    finally { setResetting(false) }
  }

  const columns = [
    {
      title: '用户名', dataIndex: 'username', key: 'username', width: 120,
      render: (t, r) => r.nickname || t || '-'
    },
    {
      title: '手机号', dataIndex: 'phone', key: 'phone', width: 130,
      render: v => v || '-'
    },
    {
      title: '会员等级', key: 'level', width: 100,
      render: (_, record) => {
        const level = getUserLevel(record)
        return <Tag color={LEVEL_COLORS[level] || 'default'}>{LEVEL_NAMES[level] || level || '基础版'}</Tag>
      }
    },
    {
      title: '角色', dataIndex: 'role', key: 'role', width: 80,
      render: v => <Tag color={ROLE_COLORS[v] || 'default'}>{ROLE_NAMES[v] || v || '会员'}</Tag>
    },
    {
      title: '到期时间', key: 'expire_at', width: 120,
      render: (_, record) => formatExpireAt(getUserExpireAt(record), record)
    },
    {
      title: '剩余天数', key: 'remaining', width: 100,
      render: (_, record) => {
        const days = calcRemainingDays(getUserExpireAt(record), record)
        return <span style={{ color: days === '已过期' ? '#ff4d4f' : undefined }}>{days}</span>
      }
    },
    {
      title: '注册时间', dataIndex: 'created_at', key: 'created_at', width: 120,
      render: v => v ? new Date(v).toLocaleDateString() : '-'
    },
    {
      title: '操作', key: 'actions', width: 100, fixed: 'right',
      render: (_, record) => (
        <Button type="link" icon={<EyeOutlined />} onClick={() => showDetail(record)}>
          查看详情
        </Button>
      )
    }
  ]

  return (
    <div>
      <Title level={4}><UserSwitchOutlined /> 会员管理</Title>
      <Card>
        <Table
          rowKey={r => r.id || r.user_id}
          columns={columns}
          dataSource={users}
          loading={loading}
          scroll={{ x: 930 }}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: t => `共 ${t} 条` }}
        />
      </Card>

      <Modal
        title="用户详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {selectedUser ? (
              <Button icon={<LockOutlined />} onClick={() => openResetPassword(selectedUser)}>
                重置密码
              </Button>
            ) : <span />}
            <Button onClick={() => setDetailVisible(false)}>关闭</Button>
          </div>
        }
        width={600}
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
        ) : selectedUser ? (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="ID">{selectedUser.id || selectedUser.user_id}</Descriptions.Item>
            <Descriptions.Item label="用户名">{selectedUser.username || '-'}</Descriptions.Item>
            <Descriptions.Item label="昵称">{selectedUser.nickname || '-'}</Descriptions.Item>
            <Descriptions.Item label="手机号">{selectedUser.phone || '-'}</Descriptions.Item>
            <Descriptions.Item label="角色">
              <Tag color={ROLE_COLORS[selectedUser.role] || 'default'}>
                {ROLE_NAMES[selectedUser.role] || selectedUser.role || '会员'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="会员等级">
              <Tag color={LEVEL_COLORS[getUserLevel(selectedUser)] || 'default'}>
                {LEVEL_NAMES[getUserLevel(selectedUser)] || getUserLevel(selectedUser) || '基础版'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="到期时间">
              {formatExpireAt(getUserExpireAt(selectedUser), selectedUser)}
            </Descriptions.Item>
            <Descriptions.Item label="剩余天数">{calcRemainingDays(getUserExpireAt(selectedUser), selectedUser)}</Descriptions.Item>
            <Descriptions.Item label="设备数上限">{selectedUser.devices || selectedUser.max_devices || '-'}</Descriptions.Item>
            <Descriptions.Item label="注册时间">
              {selectedUser.created_at ? new Date(selectedUser.created_at).toLocaleString() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="最后登录">
              {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={selectedUser.status === 'active' ? 'green' : 'default'}>
                {selectedUser.status === 'active' ? '正常' : selectedUser.status || '-'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>暂无数据</div>
        )}
      </Modal>

      <Modal
        title={`重置密码 - ${resetTarget?.username || ''}`}
        open={resetVisible}
        onCancel={() => setResetVisible(false)}
        footer={null}
        width={400}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Typography.Text type="secondary">
            为用户 <Typography.Text strong>{resetTarget?.username}</Typography.Text> 设置新密码
          </Typography.Text>
          <Input.Password
            value={resetPassword}
            onChange={e => setResetPassword(e.target.value)}
            placeholder="新密码（至少6位）"
            prefix={<LockOutlined />}
            onPressEnter={handleResetPassword}
          />
          <Button type="primary" onClick={handleResetPassword} loading={resetting} block>
            确认重置
          </Button>
        </div>
      </Modal>
    </div>
  )
}
